// ================= ВЛАСНИЙ РЕНДЕР СВІТУ (basemap) =================
// Замість чужих тайлів малюємо Оболонь самі: зелень → вода → дороги → розмітка →
// будівлі (з тінню/об'ємом) → залізниця. Повний контроль над стилем.
//
// Реалізація: кастомний L.GridLayer із canvas-тайлами. Leaflet кешує намальовані
// тайли й перемальовує лише нові — тож панорамування щокадру (камера за авто)
// не коштує нічого, на відміну від тисяч DOM/vector-об'єктів.
//
// Кольори — у THEME (токени), щоб міняти художній напрям однією правкою.
import { THEME } from './theme.js';

let world = null; // { buildings:[{g,l}], water:[{g}], green:[{g}], rail:[{g}] }
let roadWays = null; // [{n,h,l,o,g}]
let layer = null;

// ---- індекс: bbox для кожного об'єкта, щоб швидко відкидати зайве ----
function bboxOf(g) {
  let s = 90,
    w = 180,
    n = -90,
    e = -180;
  for (const p of g) {
    if (p[0] < s) s = p[0];
    if (p[0] > n) n = p[0];
    if (p[1] < w) w = p[1];
    if (p[1] > e) e = p[1];
  }
  return [s, w, n, e];
}
function prep(list) {
  for (const o of list) o.bb = bboxOf(o.g);
  return list;
}
const hits = (bb, s, w, n, e) => !(bb[2] < s || bb[0] > n || bb[3] < w || bb[1] > e);

// ширина проїзної частини за класом дороги (метри)
const ROAD_W = {
  trunk: 14,
  primary: 13,
  secondary: 11,
  tertiary: 9,
  residential: 7,
  unclassified: 7,
  living_street: 6,
  service: 4.5,
};

export function sceneryLoaded() {
  return !!world;
}

/**
 * Створити й додати шар власного світу.
 * @param {L.Map} map
 * @param {object} w   вміст world.json
 * @param {Array}  ways  масив доріг із roads.json (roads[])
 */
export function addScenery(map, w, ways) {
  world = {
    buildings: prep(w.buildings || []),
    water: prep(w.water || []),
    green: prep(w.green || []),
    rail: prep(w.rail || []),
  };
  roadWays = prep((ways || []).map((r) => ({ ...r })));

  const Scenery = L.GridLayer.extend({
    createTile: function (coords) {
      const size = this.getTileSize();
      const tile = document.createElement('canvas');
      tile.width = size.x;
      tile.height = size.y;
      try {
        drawTile(tile.getContext('2d'), coords, size, map);
      } catch (_) {}
      return tile;
    },
  });

  layer = new Scenery({ tileSize: 256, keepBuffer: 3, updateWhenIdle: false, className: 'scenery' });
  layer.addTo(map);
  layer.setZIndex(0);
  return layer;
}

// ---- малювання одного тайла (обгортка над універсальним drawWorld) ----
function drawTile(ctx, coords, size, map) {
  const z = coords.z;
  // географічні межі тайла (+ запас, щоб об'єкти на межі не «обрізались» різко)
  const nwP = coords.scaleBy(size);
  const seP = nwP.add(size);
  const nw = map.unproject(nwP, z);
  const se = map.unproject(seP, z);
  const pad = (nw.lat - se.lat) * 0.25;
  const s = se.lat - pad,
    n = nw.lat + pad,
    wl = nw.lng - pad,
    e = se.lng + pad;

  // перетворення lat/lng → пікселі всередині тайла
  const ox = nwP.x,
    oy = nwP.y;
  const P = (lat, lng) => {
    const p = map.project([lat, lng], z);
    return [p.x - ox, p.y - oy];
  };
  // метри → пікселі (для ширини доріг)
  const mPerPx = 156543.03392 * Math.cos((nw.lat * Math.PI) / 180) / Math.pow(2, z);
  const m2px = (m) => m / mPerPx;

  drawWorld(ctx, P, m2px, { s, n, w: wl, e }, size.x, size.y);
}

/**
 * Універсальний рендер світу в будь-який контекст.
 * Використовується і для тайлів карти, і для «листівки» (шер-картки).
 * @param {CanvasRenderingContext2D} ctx
 * @param {(lat:number,lng:number)=>[number,number]} P  проєкція в пікселі контексту
 * @param {(m:number)=>number} m2px  метри → пікселі
 * @param {{s:number,n:number,w:number,e:number}} bb  географічні межі кадру
 */
export function drawWorld(ctx, P, m2px, bb, W, H) {
  const s = bb.s, n = bb.n, wl = bb.w, e = bb.e;
  // 0) підкладка
  ctx.fillStyle = THEME.ground;
  ctx.fillRect(0, 0, W, H);

  const poly = (g, fill, stroke, lw) => {
    ctx.beginPath();
    for (let i = 0; i < g.length; i++) {
      const [x, y] = P(g[i][0], g[i][1]);
      i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.closePath();
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = lw || 1;
      ctx.stroke();
    }
  };

  // 1) зелень
  for (const o of world.green) if (hits(o.bb, s, wl, n, e)) poly(o.g, THEME.green);
  // 2) вода
  for (const o of world.water) {
    if (!hits(o.bb, s, wl, n, e)) continue;
    poly(o.g, THEME.water, THEME.waterEdge, 1.5);
    // халфтон-штрихування води (кешується разом із тайлом — нуль вартості на кадр)
    if (THEME.waterHatch) {
      ctx.save();
      ctx.beginPath();
      for (let i = 0; i < o.g.length; i++) {
        const [x, y] = P(o.g[i][0], o.g[i][1]);
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      ctx.closePath();
      ctx.clip();
      ctx.strokeStyle = THEME.waterHatch;
      ctx.lineWidth = Math.max(1, m2px(0.9));
      const stepPx = Math.max(6, m2px(7));
      ctx.beginPath();
      for (let d = -H; d < W + H; d += stepPx) {
        ctx.moveTo(d, 0);
        ctx.lineTo(d + H, H);
      }
      ctx.stroke();
      ctx.restore();
    }
  }

  // 3) дороги: спершу «облямівка», потім проїзна частина
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const pass of [0, 1]) {
    for (const r of roadWays) {
      if (!hits(r.bb, s, wl, n, e)) continue;
      const wm = ROAD_W[r.h] || 6;
      const wpx = m2px(wm);
      if (wpx < 0.6) continue;
      ctx.beginPath();
      for (let i = 0; i < r.g.length; i++) {
        const [x, y] = P(r.g[i][0], r.g[i][1]);
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      if (pass === 0) {
        ctx.strokeStyle = THEME.roadEdge;
        ctx.lineWidth = wpx + m2px(1.2);
      } else {
        ctx.strokeStyle = r.h === 'service' ? THEME.roadMinor : THEME.road;
        ctx.lineWidth = wpx;
      }
      ctx.stroke();
    }
  }

  // 3b) РОЗМІТКА (у тайлі, а не окремим шаром: кешується і знає тему)
  const LW = 3.0; // ширина смуги, м
  for (const r of roadWays) {
    if (r.svcFlag) continue;
    if (r.h === 'service' || r.h === 'living_street') continue;
    if (!hits(r.bb, s, wl, n, e)) continue;
    const lanes = r.l || 1;
    const pts = r.g.map((q) => P(q[0], q[1]));
    if (pts.length < 2) continue;
    // зсув полілінії на d пікселів по нормалі
    const off = (d) => {
      const out = [];
      for (let i = 0; i < pts.length; i++) {
        const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
        let nx = b[1] - a[1], ny = -(b[0] - a[0]);
        const L = Math.hypot(nx, ny) || 1;
        out.push([pts[i][0] + (nx / L) * d, pts[i][1] + (ny / L) * d]);
      }
      return out;
    };
    const line = (arr, color, wpx, dash) => {
      if (wpx < 0.4) return;
      ctx.beginPath();
      for (let i = 0; i < arr.length; i++) i ? ctx.lineTo(arr[i][0], arr[i][1]) : ctx.moveTo(arr[i][0], arr[i][1]);
      ctx.strokeStyle = color;
      ctx.lineWidth = wpx;
      ctx.setLineDash(dash || []);
      ctx.stroke();
      ctx.setLineDash([]);
    };
    const dash = [m2px(2.4), m2px(3.2)];
    if (r.o) {
      for (let k = 1; k < lanes; k++) line(off((k - lanes / 2) * m2px(LW)), THEME.lane, m2px(0.16), dash);
    } else {
      line(pts, THEME.laneCenter, m2px(lanes >= 2 ? 0.24 : 0.16), lanes >= 2 ? null : dash);
      for (let k = 1; k < lanes; k++) {
        line(off(k * m2px(LW)), THEME.lane, m2px(0.14), dash);
        line(off(-k * m2px(LW)), THEME.lane, m2px(0.14), dash);
      }
    }
  }

  // 4) залізниця
  for (const o of world.rail) {
    if (!hits(o.bb, s, wl, n, e)) continue;
    ctx.beginPath();
    for (let i = 0; i < o.g.length; i++) {
      const [x, y] = P(o.g[i][0], o.g[i][1]);
      i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.strokeStyle = THEME.rail;
    ctx.lineWidth = Math.max(1, m2px(3));
    ctx.setLineDash([m2px(4), m2px(4)]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // 5) будівлі: тінь-зсув (псевдо-об'єм за поверховістю) + корпус + дах-контур
  const sh = THEME.buildingShadowDir; // напрям тіні в пікселях на поверх
  for (const o of world.buildings) {
    if (!hits(o.bb, s, wl, n, e)) continue;
    const lift = Math.min(14, 0.9 + o.l * THEME.buildingLift);
    // НІЧ: бурштинова пляма світла на землі біля під'їзду (замість «вікон» —
    // будівлі тут footprint+бік, а не 3D-коробки, тож вікна фізично неможливі)
    if (THEME.pool && o.l >= 4) {
      let cx = 0, cy = 0;
      for (const pt of o.g) { const [x, y] = P(pt[0], pt[1]); cx += x; cy += y; }
      cx /= o.g.length; cy /= o.g.length;
      const px = cx - sh[0] * lift * 2.2, py = cy - sh[1] * lift * 2.2;
      const rad = Math.max(m2px(9), lift * 3.4);
      const gd = ctx.createRadialGradient(px, py, 0, px, py, rad);
      gd.addColorStop(0, THEME.pool);
      gd.addColorStop(1, 'rgba(255,184,77,0)');
      ctx.fillStyle = gd;
      ctx.beginPath();
      ctx.arc(px, py, rad, 0, 6.2832);
      ctx.fill();
    }
    // тінь/бік
    ctx.beginPath();
    for (let i = 0; i < o.g.length; i++) {
      const [x, y] = P(o.g[i][0], o.g[i][1]);
      i ? ctx.lineTo(x + sh[0] * lift, y + sh[1] * lift) : ctx.moveTo(x + sh[0] * lift, y + sh[1] * lift);
    }
    ctx.closePath();
    ctx.fillStyle = THEME.buildingSide;
    ctx.fill();
    // корпус (уночі кант теплий — «освітлений край»)
    poly(o.g, THEME.building, THEME.buildingEdge, THEME.night ? 1.4 : 1);
  }
}

/**
 * Намалювати ділянку світу навколо точки у власний canvas (для листівки).
 * @param {number} lat @param {number} lng  центр
 * @param {number} W @param {number} H  розмір у пікселях
 * @param {number} mPerPx  метрів на піксель (менше = ближче)
 */
export function drawRegion(ctx, lat, lng, W, H, mPerPx) {
  if (!world) return false;
  const K = 111320, KX = K * Math.cos((lat * Math.PI) / 180);
  const P = (la, ln) => [W / 2 + ((ln - lng) * KX) / mPerPx, H / 2 - ((la - lat) * K) / mPerPx];
  const halfLat = ((H / 2) * mPerPx) / K, halfLng = ((W / 2) * mPerPx) / KX;
  drawWorld(ctx, P, (m) => m / mPerPx, { s: lat - halfLat, n: lat + halfLat, w: lng - halfLng, e: lng + halfLng }, W, H);
  return true;
}

/** Перемалювати світ (після зміни теми). */
export function refreshScenery() {
  try {
    if (layer) layer.redraw();
  } catch (_) {}
}
